import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type RML,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { useInitialData } from '@post-office/message-maker-browser/use-initial-data';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { useEffect, useState } from 'react';

export type PlacementRenderProps = {
	onDismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export const PLACEMENT_ID = 'agent-studio-page-pre-content' as const;

const placementConfig: PlacementBrowserConfig = {
	placementId: PLACEMENT_ID,
	options: {
		choreographer: {
			isDefaultEnabled: true, // setting to true as it should allow only one message at a time (https://developer.atlassian.com/platform/post-office/working-with-post-office/guides/in-app-v2/#1--create-your-new-placement)
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	RML
> = createFrontend<RML>((Messages) => ({ initialData }) => {
	const { data, error, isLoading } = useInitialData<DefaultMessagesResponse>(initialData);
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);

	useEffect(() => {
		if (isLoading) {
			return;
		}

		if (error) {
			return;
		}

		if (!data) {
			return;
		}

		setMessages(data.messages);
	}, [data, error, isLoading]);

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);

			return filteredMessages;
		});
	};

	if (error || isLoading || messages.length === 0) {
		return null;
	}

	// Extra protection in case choreographer is not working. We should show one message at a time.
	const messageToRender = messages[0];

	return (
		<>
			{messageToRender ? (
				<Messages.render
					key={messageToRender.messageInstanceId}
					onDismiss={() => handleDismiss(messageToRender.messageInstanceId)}
					{...messageToRender}
				/>
			) : null}
		</>
	);
});
